import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, RotateCcw } from 'lucide-react';
import { useModules } from '../contexts/ModuleContext';
import { useAuth } from '../contexts/AuthContext';
import { graphqlService } from '../services/graphqlService';

const Quiz = () => {
  const { moduleId } = useParams();
  const { getModule } = useModules();
  const { user } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
  const [quizStarted, setQuizStarted] = useState(false);

  const module = getModule(moduleId);

  // Load existing progress to restore selected answers
  useEffect(() => {
    if (!module || !user) return;

    const loadProgress = async () => {
      try {
        const progress = await graphqlService.getProgressByModule(user.email, moduleId);
        if (progress && progress.selectedAnswers) {
          // Convert selectedAnswers from object to the format we need
          const answers = typeof progress.selectedAnswers === 'object' 
            ? progress.selectedAnswers 
            : {};
          setSelectedAnswers(answers);
        }
      } catch (error) {
        console.error('Error loading quiz progress:', error);
      }
    };

    loadProgress();
  }, [moduleId, module, user]);

  useEffect(() => {
    if (!quizStarted) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          handleSubmitQuiz();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [quizStarted]);

  if (!module) {
    return (
      <div className="container">
        <div className="card text-center">
          <h2>Module not found</h2>
          <Link to="/" className="btn btn-primary">Return to Dashboard</Link>
        </div>
      </div>
    );
  }

  const startQuiz = () => {
    setQuizStarted(true);
  };

  const handleAnswerSelect = (questionIndex, answerIndex) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionIndex]: answerIndex
    }));
  };

  const handleNext = () => {
    if (currentQuestion < module.quiz.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmitQuiz = async () => {
    let correctAnswers = 0;
    module.quiz.forEach((question, index) => {
      if (selectedAnswers[index] === question.correct) {
        correctAnswers++;
      }
    });

    const percentage = Math.round((correctAnswers / module.quiz.length) * 100);
    setScore(percentage);
    setShowResults(true);
    setQuizStarted(false);

    // Save quiz results to database
    if (user && user.email) {
      try {
        await graphqlService.createOrUpdateProgress({
          userEmail: user.email,
          moduleId: moduleId,
          quizScore: percentage,
          quizCompleted: true,
          quizCompletedAt: new Date().toISOString(),
          selectedAnswers: selectedAnswers
        });
      } catch (error) {
        console.error('Error saving quiz results:', error);
      }
    }
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setShowResults(false);
    setScore(0);
    setTimeLeft(300);
    setQuizStarted(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!quizStarted && !showResults) {
    return (
      <div className="container">
        <Link to={`/module/${moduleId}`} className="btn btn-secondary back-to-dashboard">
          <ArrowLeft size={16} style={{ marginRight: '8px' }} />
          Back to Module
        </Link>
        
        <div className="quiz-info">
          <div className="module-icon-large">
            {React.isValidElement(module.icon) ? module.icon : null}
          </div>
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#0477aa' }}>{module.title} Quiz</h1>
            <p className="text-lg text-gray-600">Test your knowledge</p>
          </div>
        </div>

        <div className="card quiz-instructions">
          <h2 className="text-2xl font-bold mb-4">Quiz Instructions</h2>
          <ul className="instructions-list">
            <li>You have 5 minutes to complete the quiz</li>
            <li>Answer all questions to the best of your ability</li>
            <li>You can navigate between questions using Previous/Next buttons</li>
            <li>Your answers are saved as you go</li>
            <li>You need 70% or higher to pass</li>
            <li>You can retake the quiz if needed</li>
          </ul>
          
          <button onClick={startQuiz} className="btn btn-primary btn-large">
            Start Quiz
          </button>
        </div>
      </div>
    );
  }

  if (showResults) {
    const passed = score >= 70;
    
    return (
      <div className="container">
        <div className="card results-card">
          <div className="results-header">
            <div className={`results-icon ${passed ? 'passed' : 'failed'}`}>
              {passed ? <CheckCircle size={64} color="#059669" /> : <XCircle size={64} color="#ef4444" />}
            </div>
            <h2 className="text-3xl font-bold mb-4">
              {passed ? 'Congratulations!' : 'Try Again'}
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              You scored <strong>{score}%</strong> on the {module.title} quiz
            </p>
          </div>

          <div className="score-breakdown">
            <div className="score-item">
              <span>Correct Answers:</span>
              <span>{Math.round((score / 100) * module.quiz.length)}/{module.quiz.length}</span>
            </div>
            <div className="score-item">
              <span>Passing Score:</span>
              <span>70%</span>
            </div>
            <div className="score-item">
              <span>Your Score:</span>
              <span className={passed ? 'text-green-600' : 'text-red-600'}>{score}%</span>
            </div>
          </div>

          <div className="results-actions">
            {!passed && (
              <button onClick={resetQuiz} className="btn btn-primary">
                <RotateCcw size={16} style={{ marginRight: '8px' }} />
                Retake Quiz
              </button>
            )}
            <Link to={`/module/${moduleId}`} className="btn btn-secondary">
              Review Module
            </Link>
            <Link to="/" className="btn btn-primary">
              Return to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const currentQ = module.quiz[currentQuestion];
  const progress = ((currentQuestion + 1) / module.quiz.length) * 100;

  return (
    <div className="container">
      <div className="quiz-header">
        <div className="quiz-timer">
          Time: {formatTime(timeLeft)}
        </div>
        
        <div className="quiz-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <p className="text-sm text-gray-600">
            Question {currentQuestion + 1} of {module.quiz.length}
          </p>
        </div>
      </div>

      <div className="card quiz-question">
        <h2 className="text-xl font-bold mb-6">{currentQ.question}</h2>
        
        <div className="quiz-options">
          {currentQ.options.map((option, index) => (
            <label 
              key={index} 
              className={`option-label ${selectedAnswers[currentQuestion] === index ? 'selected' : ''}`}
            >
              <input
                type="radio"
                name={`question-${currentQuestion}`}
                value={index}
                checked={selectedAnswers[currentQuestion] === index}
                onChange={() => handleAnswerSelect(currentQuestion, index)}
              />
              <span className="option-text">{option}</span>
            </label>
          ))}
        </div>

        <div className="quiz-navigation">
          <button 
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
            className="btn btn-secondary"
          >
            Previous
          </button>
          
          {currentQuestion === module.quiz.length - 1 ? (
            <button onClick={handleSubmitQuiz} className="btn btn-success">
              Submit Quiz
            </button>
          ) : (
            <button onClick={handleNext} className="btn btn-primary">
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Quiz;
